import { memo, useState } from 'react';
import moment from 'moment';

// ── Helper ───────────────────────────────────────────────────────────

function formatDateTime(dateTime: any): string {
  if (!dateTime) return '';
  return moment(dateTime).format('YYYY-MM-DD HH:mm');
}

function getCompletionClass(stop: any): string {
  if (stop.numTimesGuardTourStopCompleted >= stop.numTimesToCompleteGuardTourStop) {
    return 'complete';
  }
  if (stop.numTimesGuardTourStopCompleted > 0) {
    return 'partial';
  }
  return 'pending';
}

function getInactiveBadge(stop: any): { className: string; text: string } | null {
  if (!stop.inactiveReason) return null;
  if (stop.inactiveReason === 'PAUSE_WINDOW') {
    const until = stop.inactiveUntil ? moment(stop.inactiveUntil).format('YYYY-MM-DD HH:mm') : '';
    return { className: 'paused', text: until ? `Paused until ${until}` : 'Paused' };
  }
  if (stop.inactiveReason === 'CALENDAR_SATURDAY') {
    return { className: 'calendar', text: 'Not active on Saturdays' };
  }
  if (stop.inactiveReason === 'CALENDAR_SUNDAY') {
    return { className: 'calendar', text: 'Not active on Sundays' };
  }
  if (stop.inactiveReason === 'CALENDAR_DAY_OF_WEEK') {
    return { className: 'calendar', text: 'Not scheduled today' };
  }
  if (stop.inactiveReason === 'CALENDAR_EVEN_DAYS') {
    return { className: 'calendar', text: 'Not active today (even-day schedule)' };
  }
  if (stop.inactiveReason === 'CALENDAR_ODD_DAYS') {
    return { className: 'calendar', text: 'Not active today (odd-day schedule)' };
  }
  // Unknown reason from a newer backend: still surface as inactive so the row is non-completable,
  // but fall back to a generic label rather than treating it as a fully active stop.
  return { className: 'calendar', text: 'Not active' };
}

// All inactive stops collapse into a single summary entry — officers shouldn't have to scroll
// past stops they can't act on. Whatever the reason (calendar constraint, pause window, or a
// future reason from a newer backend), keep it out of the main flow until the officer expands.
function isCollapsibleReason(reason: string | null | undefined): boolean {
  return typeof reason === 'string' && reason.length > 0;
}

// ── Component ────────────────────────────────────────────────────────

interface TourStopsSidebarProps {
  currentTour: any;
  currentStop: any;
  filteredStops: any[];
  stopFilter: string;
  isLoadingStops: boolean;
  isMobile: boolean;
  showStopsList: boolean;
  timeRemaining: string | null;
  isTimeUrgent: boolean;
  onSetStopFilter: (value: string) => void;
  onSelectStop: (stop: any) => void;
}

export const TourStopsSidebar = memo(function TourStopsSidebar({
  currentTour,
  currentStop,
  filteredStops,
  stopFilter,
  isLoadingStops,
  isMobile,
  showStopsList,
  timeRemaining,
  isTimeUrgent,
  onSetStopFilter,
  onSelectStop,
}: TourStopsSidebarProps) {
  const [showCalendarInactive, setShowCalendarInactive] = useState(false);

  const inlineStops = filteredStops.filter((s: any) => !isCollapsibleReason(s.inactiveReason));
  const collapsedStops = filteredStops.filter((s: any) => isCollapsibleReason(s.inactiveReason));

  return (
    <div className={`tour-stops-sidebar${isMobile && showStopsList ? ' visible' : ''}`}>
      {/* Tour Summary */}
      <div className="tour-summary">
        <h2 className="tour-summary-title">{currentTour.title}</h2>
        <div className="tour-summary-meta">
          <div>
            <strong>Start:</strong> {formatDateTime(currentTour.MinStartTime)}
          </div>
          <div>
            <strong>End:</strong> {formatDateTime(currentTour.MaxEndTime)}
          </div>
          {currentTour.officerNames && currentTour.officerNames.length > 0 && (
            <div>
              <strong>Assigned:</strong> {currentTour.officerNames.join(', ')}
            </div>
          )}
        </div>
        {timeRemaining && (
          <div className={`tour-time-badge${isTimeUrgent ? ' urgent' : ''}`}>
            <i className="fa fa-clock"></i>
            {timeRemaining}
          </div>
        )}
      </div>

      {/* Stop Filter */}
      <div className="stop-filter">
        <input
          type="text"
          className="stop-filter-input"
          placeholder="Filter by title or location..."
          value={stopFilter}
          onChange={(e) => onSetStopFilter(e.target.value)}
        />
      </div>

      {/* Stop List */}
      <div className="stop-list">
        {isLoadingStops ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <div>Loading stops...</div>
          </div>
        ) : (
          <>
            {inlineStops.map((stop: any) => {
              const inactiveBadge = getInactiveBadge(stop);
              const isInactive = !!inactiveBadge;
              return (
                <div
                  key={stop.id}
                  className={`stop-item${currentStop && currentStop.id === stop.id ? ' active' : ''}${stop.numTimesGuardTourStopCompleted >= stop.numTimesToCompleteGuardTourStop ? ' completed' : ''}${isInactive ? ' inactive' : ''}`}
                  onClick={isInactive ? undefined : () => onSelectStop(stop)}
                >
                  <h4 className="stop-title">{stop.title}</h4>
                  <p className="stop-location">{stop.location}</p>
                  <div className="stop-status">
                    <span className={`stop-completion-badge ${getCompletionClass(stop)}`}>
                      {stop.numTimesGuardTourStopCompleted} / {stop.numTimesToCompleteGuardTourStop}
                    </span>
                    {inactiveBadge && (
                      <span className={`stop-inactive-badge ${inactiveBadge.className}`}>
                        {inactiveBadge.text}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
            {collapsedStops.length > 0 && (
              <div className="stop-summary-collapsed-wrapper">
                <button
                  type="button"
                  className={`stop-summary-collapsed${showCalendarInactive ? ' expanded' : ''}`}
                  onClick={() => setShowCalendarInactive(!showCalendarInactive)}
                  aria-expanded={showCalendarInactive}
                >
                  <span className="stop-summary-collapsed-text">
                    {collapsedStops.length === 1
                      ? '1 stop not active today'
                      : `${collapsedStops.length} stops not active today`}
                  </span>
                  <i className={`fa fa-chevron-${showCalendarInactive ? 'up' : 'down'}`}></i>
                </button>
                {showCalendarInactive && (
                  <div className="stop-summary-collapsed-list">
                    {collapsedStops.map((stop: any) => {
                      const badge = getInactiveBadge(stop);
                      return (
                        <div key={stop.id} className="stop-summary-collapsed-row">
                          <span className="stop-summary-collapsed-row-title">{stop.title}</span>
                          <span className="stop-summary-collapsed-row-reason">{badge?.text}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
});
